import React, { Suspense, useMemo } from 'react'

import { 
  usePostEnergyTreeNodesMutation, 
  useDeleteEnergyTreeNodesMutation,
  usePutEnergyTreeNodesMutation
} from '../../../../api/recalcV2/endpoints/energyTreeNodesApiSlice';

import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../../redux/snackbarSlice';

import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { Box, Typography } from '@mui/material';

const ConsumptionTreeGraph = React.lazy(() => import('remote/ConsumptionTreeGraph').catch(() => ({ default: () => <div>Failed to load</div> })));

function parseScheduleOptions(options) {
  if (!options || !options.workingDayStart || !options.workingDayEnd) return null;
  const { workingDayStart, workingDayEnd, daysOfWeek = {} } = options;

  const startHour = workingDayStart;
  const endHour = workingDayEnd;

  const dayMapping = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6
  };

  const activeDays = Object.entries(daysOfWeek)
  .filter(([_, isActive]) => isActive)
  .map(([day]) => dayMapping[day.toLowerCase()])
  .filter((index) => index !== undefined);

  return {
    startHour,
    endHour,
    activeDays,
  };
}

const augmentSelectedEnergyGroup = (selectedEnergyGroup, unitDataResults, sliceMode, schedule) => {
  if (
    !selectedEnergyGroup?.energyTreeNodes ||
    !unitDataResults ||
    unitDataResults.every(item => item == null)
  ) {
    return selectedEnergyGroup;
  }

  const isWithinInterval = (hour, [start, end]) => {
    if (start <= end) {
      return hour >= start && hour < end;
    } else {
      return hour >= start || hour < end;
    }
  };

  const filterDataBySliceMode = (data) => {
    return data.filter(({ Timestamp }) => {
      const dateObj = new Date(Timestamp);
      const hour = dateObj.getHours();
      const day = dateObj.getDay();

      switch (sliceMode) {
        case "Aktive Dage":
          return day >= 1 && day <= 5;
        case "Inaktive Dage":
          return day === 0 || day === 6;
        case "Aktive Timer":
          return isWithinInterval(hour, [schedule.startHour, schedule.endHour]);
        case "Inaktive Timer":
          return isWithinInterval(hour, [schedule.endHour, schedule.startHour]);
        default:
          return true;
      }
    });
  };

  const convertToWatts = (data) => {
    if (data.Base === "kWh" && !data.converted) {
      return { ...data, Base: "W", Value: data.Value * 1000, converted: true };
    }
    return data;
  };

  const unitDataMap = unitDataResults.reduce((map, item) => {
    const unitId = item?.unit?.UnitId;
    if (unitId != null) {
      map[unitId] = {
        unit: item.unit,
        data: filterDataBySliceMode(item.data || []).map(convertToWatts),
      };
    }
    return map;
  }, {});

  const flatAugmentedNodes = selectedEnergyGroup.energyTreeNodes.map((node) => {
    const associatedUnits = node.dataStreams?.map(ds => unitDataMap[ds.unitId]?.unit).filter(Boolean) || [];
    const associatedData = node.dataStreams?.flatMap(ds => unitDataMap[ds.unitId]?.data || []) || [];
    const totalConsumption = associatedData.reduce((sum, entry) => sum + entry.Value, 0);

    return {
      ...node,
      options: {
        ...node.options,
        units: associatedUnits,
        data: associatedData,
        totalConsumption,
      },
    };
  });

  return {
    ...selectedEnergyGroup,
    energyTreeNodes: flatAugmentedNodes, // ✅ Flat array for OrgChart
  };
};

const EnergyTreeGraphTab = ({
  selectedEnergyGroup,
  unitDataResults,
  units,
  setSelectedSliceMode,
  selectedSliceMode,
  setNodeToAnalyse,
  setValue,
  isLoading
}) => {

  const dispatch = useDispatch();

  const [postEnergyTreeNodes] = usePostEnergyTreeNodesMutation();
  const [deleteEnergyTreeNodes] = useDeleteEnergyTreeNodesMutation();
  const [putEnergyTreeNodes] = usePutEnergyTreeNodesMutation();


  const handleAddNode = async (newNode) => {
    try {
      const createdNode = await postEnergyTreeNodes({ energyTreeNode: newNode, accountId: selectedEnergyGroup.accountId }).unwrap();
      dispatch(showSnackbar({ message: 'Node tilføjet', severity: 'success' }));
    } catch (error) {
      dispatch(showSnackbar({ message: 'Der opstod en fejl i tilføjelse af node', severity: 'error' }));
    }
  };

  const handleDeleteNode = async (energyTreeNodeId) => {
    try {
      const response = await deleteEnergyTreeNodes({ accountId: selectedEnergyGroup.accountId, energyTreeNodeId }).unwrap();
      dispatch(showSnackbar({ message: 'Node fjernet', severity: 'success' }));
    } catch (error) {
      dispatch(showSnackbar({ message: 'Der opstod en fejl i fjernelse af node', severity: 'error', response: error }));

    }
  };

  const handleUpdateNode = async (nodeId, updates) => {
    try {
      const response = await putEnergyTreeNodes({ accountId: selectedEnergyGroup.accountId, energyTreeNodeId: nodeId, updatedEnergyTreeNode: updates }).unwrap();
      dispatch(showSnackbar({ message: 'Node opdateret', severity: 'success' }));
    } catch (error) {
      console.error('Failed to update node:', error);
      dispatch(showSnackbar({ message: 'Der opstod en fejl i opdatering af node', severity: 'error', response: error }));
    }
  };

  const schedule = useMemo(() => {
    if (!selectedEnergyGroup) return null;
    return parseScheduleOptions(selectedEnergyGroup.options);
  }, [selectedEnergyGroup]);

  const augmentedEnergyGroup = useMemo(() => {
    return augmentSelectedEnergyGroup(selectedEnergyGroup, unitDataResults, selectedSliceMode, schedule);
  }, [selectedEnergyGroup, unitDataResults, selectedSliceMode]);

  return (
    <>
      {isLoading &&
        <Backdrop
          sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
          open={isLoading}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularProgress color="inherit" />
            <Typography variant="h6" sx={{ mt: 1 }}>
              Indlæser...
            </Typography>
          </Box>
        </Backdrop>
      }
      <Suspense fallback={<div>Loading TreeComponent...</div>}>
        <ConsumptionTreeGraph
          selectedEnergyGroup={augmentedEnergyGroup || {}}
          units={units}
          data={augmentedEnergyGroup?.energyTreeNodes || []}
          onUpdateNode={handleUpdateNode}
          onAddNode={handleAddNode}
          onDeleteNode={handleDeleteNode}
          selectedSliceMode={selectedSliceMode}
          setSelectedSliceMode={setSelectedSliceMode}
          setNodeToAnalyse={setNodeToAnalyse}
          setValue={setValue}
        />
      </Suspense>
    </>
  )
}

export default EnergyTreeGraphTab